import React, { useEffect, useState } from "react";
import { Radio, ShieldAlert, Lock, Activity, Database, EyeOff, Globe2 } from "lucide-react";

/**
 * JerichoCommandRail — left vertical tactical readout.
 * No "card" background — sits directly on the environment with thin
 * vertical divider strips, like a HUD overlay on a black-site monitor.
 */
export const JerichoCommandRail = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const utc = now.toISOString().replace("T", " ").slice(0, 19) + "Z";
  const local = now.toLocaleTimeString([], { hour12: false });

  // pseudo coords (fixed seed-ish)
  const lat = "39°48′24″N";
  const lon = "117°02′41″W";

  return (
    <aside
      data-testid="jericho-command-rail"
      className="relative pl-3 sm:pl-4 pr-2"
      style={{ borderLeft: "1px solid rgba(217,45,32,0.35)" }}
    >
      {/* Soft top tag line */}
      <div className="absolute -left-px top-0 w-px h-12 bg-gradient-to-b from-[#D92D20] to-transparent" />
      <div className="absolute -left-px bottom-0 w-px h-12 bg-gradient-to-t from-[#5DD4B3] to-transparent" />

      {/* Operator */}
      <Block label="OPERATOR" code="OP-04">
        <div className="font-display text-[15px] text-[#E4DFD3] leading-tight">
          CALLSIGN — JERICHO·01
        </div>
        <div className="mt-1 font-mono text-[10px] tracking-[0.2em] text-[#5DD4B3]">
          CLEARANCE · OMEGA
        </div>
        <div className="mt-0.5 font-mono text-[9px] tracking-[0.2em] text-[#7a7062]">
          BADGE · 0X4F2A·B7
        </div>
      </Block>

      {/* Position */}
      <Block label="POSITION" code="GEO-2">
        <div className="font-mono text-[10.5px] text-[#E4DFD3] tracking-[0.15em]">{lat}</div>
        <div className="font-mono text-[10.5px] text-[#E4DFD3] tracking-[0.15em]">{lon}</div>
        <div className="mt-0.5 font-mono text-[9px] tracking-[0.2em] text-[#A39E93]">
          ELEV · 1,427 m
        </div>
      </Block>

      {/* Chrono */}
      <Block label="CHRONO" code="T-0">
        <div className="font-mono text-[10.5px] text-[#E4DFD3] tracking-[0.15em]">{utc}</div>
        <div className="font-mono text-[10px] text-[#A39E93] tracking-[0.2em] mt-0.5">
          LOCAL · {local}
        </div>
      </Block>

      {/* Systems lamps */}
      <Block label="SYSTEMS" code="SYS">
        <Lamp label="UPLINK" tone="ok" value="SECURE" />
        <Lamp label="ENCRYPT" tone="ok" value="AES-256" />
        <Lamp label="GRID" tone="warn" value="ROUTED" />
        <Lamp label="TRACE" tone="off" value="MASKED" />
      </Block>

      {/* Command */}
      <div className="mb-1">
        <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#D92D20] mb-1.5">
          // COMMAND
        </div>
        <CommandItem icon={ShieldAlert} label="BLACK-SITE OVERVIEW" active />
        <CommandItem icon={Database} label="DOSSIER VAULT" />
        <CommandItem icon={Radio} label="SURVEILLANCE FEED" />
        <CommandItem icon={Activity} label="EVIDENCE TRAFFIC" />
        <CommandItem icon={Globe2} label="GEO-INDEX" />
        <CommandItem icon={EyeOff} label="REDACTION QUEUE" disabled />
        <CommandItem icon={Lock} label="SEALED DRAWERS" disabled />
      </div>

      <div className="pt-3 mt-3 border-t border-[#2A2722] font-mono text-[9px] uppercase tracking-[0.3em] text-[#5a544a]">
        NODE · 01 / 07
      </div>
    </aside>
  );
};

const Block = ({ label, code, children }) => (
  <div className="mb-4 pb-3 border-b border-[#1f1d1a]/80">
    <div className="flex items-center justify-between mb-1">
      <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#7a7062]">
        // {label}
      </div>
      <div className="font-mono text-[8.5px] uppercase tracking-[0.3em] text-[#3a342c]">
        {code}
      </div>
    </div>
    {children}
  </div>
);

const Lamp = ({ label, tone, value }) => {
  const dot = {
    ok: "bg-[#5DD4B3] shadow-[0_0_6px_rgba(93,212,179,0.7)]",
    warn: "bg-[#E0A526] shadow-[0_0_6px_rgba(224,165,38,0.7)]",
    off: "bg-[#454038]",
    alert: "bg-[#D92D20] shadow-[0_0_6px_rgba(217,45,32,0.7)]",
  }[tone];
  const txt = {
    ok: "text-[#5DD4B3]",
    warn: "text-[#E0A526]",
    off: "text-[#7a7062]",
    alert: "text-[#D92D20]",
  }[tone];
  return (
    <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.2em] mb-0.5">
      <div className="flex items-center gap-2">
        <span className={`block w-1.5 h-1.5 rounded-full ${dot}`} />
        <span className="text-[#A39E93]">{label}</span>
      </div>
      <span className={txt}>{value}</span>
    </div>
  );
};

const CommandItem = ({ icon: Icon, label, active = false, disabled = false }) => (
  <button
    type="button"
    disabled={disabled}
    className={
      "w-full flex items-center gap-2 px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border-l-2 transition-colors " +
      (active
        ? "border-[#D92D20] text-[#E4DFD3] bg-[#D92D20]/[0.10]"
        : disabled
        ? "border-transparent text-[#454038] cursor-not-allowed"
        : "border-transparent text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#D92D20]/60 hover:bg-white/[0.03]")
    }
  >
    <Icon size={11} className="shrink-0" />
    <span className="truncate text-left">{label}</span>
  </button>
);

export default JerichoCommandRail;
